import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { SUPPORTED_LANGUAGES } from '../../i18n';

/**
 * Compact language setting for the authenticated Profile/Settings surface.
 *
 * Reuses the same i18next `changeLanguage` flow and `localStorage`
 * persistence as the login-screen switcher (Fas 1). No user-model field is
 * introduced; storage remains browser-local.
 */
export default function LanguageSettingCard() {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const current = SUPPORTED_LANGUAGES.includes(i18n.language) ? i18n.language : 'sv';

  const onChange = (e) => {
    const next = e.target.value;
    if (next === current) return;
    i18n.changeLanguage(next);
    toast.success(t('settings:language.toast_switched'));
  };

  return (
    <Card className="bg-card border-border" data-testid="settings-language-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading flex items-center gap-2 text-base">
          <Globe2 className="w-4 h-4 text-primary" />
          {t('settings:language.heading')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-3">
          {t('settings:language.description')}
        </p>
        <label htmlFor="settings-language-select" className="sr-only">
          {t('common:language.aria')}
        </label>
        <select
          id="settings-language-select"
          value={current}
          onChange={onChange}
          aria-label={t('common:language.aria')}
          data-testid="settings-language-select"
          className="w-full max-w-xs bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40 cursor-pointer"
        >
          {SUPPORTED_LANGUAGES.map((code) => (
            <option key={code} value={code}>
              {t(`common:language.${code}`)}
            </option>
          ))}
        </select>
      </CardContent>
    </Card>
  );
}
